// useViewReporter.ts — counts a view after 3 seconds of real playback (owner rule: a view
// = 3+ s watched, once per person per day — the "once per day" half is server-side).
//
// Only time that PLAYS counts: seeking, scrubbing and paused time do not, so dragging the
// playhead to the end is not a view. Reports at most once per mounted player + clip.

import { useEffect, useRef, type RefObject } from 'react';
import { videoFeedService } from '@/core/services/videoFeedService';

export const VIEW_AFTER_SECONDS = 3;
/** A gap larger than this between two timeupdates is a seek, not playback. */
const MAX_TICK_SECONDS = 1.5;

export function useViewReporter(
  ref: RefObject<HTMLVideoElement | null>,
  videoId: string | undefined,
  onCounted?: (views: number) => void,
): void {
  const onCountedRef = useRef(onCounted);
  useEffect(() => {
    onCountedRef.current = onCounted;
  }, [onCounted]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !videoId) return;
    let watched = 0;
    let last: number | null = null;
    let sent = false;
    const onTime = () => {
      if (sent) return;
      const now = el.currentTime;
      if (last !== null && !el.paused) {
        const delta = now - last;
        if (delta > 0 && delta < MAX_TICK_SECONDS) watched += delta;
      }
      last = now;
      if (watched >= VIEW_AFTER_SECONDS) {
        sent = true;
        void videoFeedService.view(videoId).then((views) => {
          if (views !== null) onCountedRef.current?.(views);
        });
      }
    };
    const onJump = () => {
      last = el.currentTime;
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('seeking', onJump);
    el.addEventListener('play', onJump);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('seeking', onJump);
      el.removeEventListener('play', onJump);
    };
  }, [ref, videoId]);
}
